import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, KPI, DataTable, GhostButton, PrimaryButton } from "@/components/Primitives";
import StatusPill from "@/components/StatusPill";
import { useNavigate } from "react-router-dom";
import { ArrowClockwise, Robot, Warning, ArrowSquareOut } from "@phosphor-icons/react";

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");

export default function AutomationOpsPanel({ reloadSignal, onRunDue, dueBusy }) {
  const [act, setAct] = useState(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/search-runs/automation-activity");
      setAct(r.data);
    } catch (e) { /* internal-only; keep prior state on failure */ }
  };

  useEffect(() => {
    let active = true;
    (async () => { await load(); if (!active) return; })();
    return () => { active = false; };
  }, [reloadSignal]);

  const s = act?.summary;

  return (
    <section data-testid="automation-ops-panel">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-1.5">
          <Robot size={12} /> Discovery Operations · Automation Activity
        </div>
        <div className="flex items-center gap-2">
          <GhostButton onClick={load} data-testid="ops-refresh-btn"><ArrowClockwise size={11} className="inline mr-1" />Refresh</GhostButton>
          <GhostButton onClick={() => nav("/candidates")} data-testid="ops-view-queue-btn"><ArrowSquareOut size={11} className="inline mr-1" />Review Queue</GhostButton>
          <GhostButton onClick={() => nav("/search-runs")} data-testid="ops-view-runs-btn"><ArrowSquareOut size={11} className="inline mr-1" />Search Runs</GhostButton>
          <PrimaryButton onClick={onRunDue} disabled={dueBusy} data-testid="ops-run-due-btn">{dueBusy ? "Running…" : "Run Due Now"}</PrimaryButton>
        </div>
      </div>

      {/* A. Health summary */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="ops-health">
        <KPI label="Automation On" value={s?.automation_enabled_rules ?? 0} />
        <KPI label="Due Now" value={s?.rules_due_now ?? 0} accent />
        <KPI label="Overdue" value={s?.rules_overdue ?? 0} hint={`${s?.rules_due_soon ?? 0} due soon`} />
        <KPI label="Created 24h" value={s?.candidates_created_24h ?? 0} hint={`${s?.candidates_created_7d ?? 0} in 7d`} />
        <KPI label="Failures 7d" value={s?.failures_7d ?? 0} />
        <div className="border border-white/10 p-3" data-testid="ops-last-scheduled">
          <div className="text-[9px] uppercase tracking-widest text-zinc-500">Last Scheduled</div>
          <div className="mt-1">{s?.last_scheduled_run_status ? <StatusPill value={s.last_scheduled_run_status} /> : <span className="text-zinc-600 text-xs">None yet</span>}</div>
          <div className="text-[10px] text-zinc-500 font-mono-data mt-1">{fmtDateTime(s?.last_scheduled_run_at)}</div>
        </div>
      </div>

      {/* B. Recent runs */}
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Recent TED Runs</div>
      <div data-testid="ops-recent-runs-table" className="mb-5">
        <DataTable
          onRowClick={() => nav("/search-runs")}
          emptyText={act === null ? "Loading…" : "No TED runs yet — enable automation or run a manual TED search."}
          columns={[
            { key: "started_at", label: "Started", render: (r) => <span className="font-mono-data text-[11px]">{fmtDateTime(r.started_at)}</span> },
            { key: "run_type", label: "Type", render: (r) => <StatusPill value={r.run_type === "Scheduled TED" ? "Scheduled" : r.run_type} /> },
            { key: "rule_name", label: "Rule" },
            { key: "run_status", label: "Status", render: (r) => <StatusPill value={r.run_status} /> },
            { key: "candidates_created", label: "New", mono: true },
            { key: "possible_duplicates_created", label: "Poss", mono: true },
            { key: "exact_duplicates_skipped", label: "Skip", mono: true },
            { key: "warnings_count", label: "Warn", mono: true },
            { key: "errors_count", label: "Err", mono: true },
            { key: "run_by", label: "Run By" },
          ]}
          rows={act?.recent_runs || []}
        />
      </div>

      {/* C. Rule schedule */}
      <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 mb-2">Rule Schedule</div>
      <div data-testid="ops-rule-schedule-table" className="mb-5">
        <DataTable
          onRowClick={() => nav("/search-rules")}
          emptyText="No rules"
          columns={[
            { key: "rule_name", label: "Rule" },
            { key: "automation_enabled", label: "Auto", render: (r) => <StatusPill value={r.automation_enabled ? "Yes" : "No"} /> },
            { key: "search_frequency", label: "Freq" },
            { key: "due_state", label: "Schedule", render: (r) => <StatusPill value={r.due_state} /> },
            { key: "next_run_at", label: "Next Run", render: (r) => <span className="font-mono-data text-[11px] text-zinc-400">{fmtDate(r.next_run_at)}</span> },
            { key: "last_automated_run_at", label: "Last Auto", render: (r) => <span className="font-mono-data text-[11px] text-zinc-400">{fmtDate(r.last_automated_run_at)}</span> },
            { key: "last_automated_run_status", label: "Last Status", render: (r) => r.last_automated_run_status ? <StatusPill value={r.last_automated_run_status} /> : <span className="text-zinc-600">—</span> },
            { key: "last_automated_result_count", label: "Results", mono: true, render: (r) => <span className="font-mono-data text-[11px]">{r.last_automated_result_count ?? "—"}</span> },
          ]}
          rows={act?.rule_schedule || []}
        />
      </div>

      {/* D. Failures / warnings */}
      {act?.failures_warnings?.length > 0 && (
        <Card className="p-4 border-[#FFB300]/30" data-testid="ops-failures-list">
          <div className="text-[10px] uppercase tracking-widest text-[#FFB300] mb-2 flex items-center gap-1"><Warning size={12} /> Recent Failures & Warnings</div>
          <div className="divide-y divide-white/5">
            {act.failures_warnings.map((f) => (
              <div key={f.run_id} className="py-2 flex items-start justify-between gap-3 text-[11px]" data-testid={`ops-failure-${f.run_id}`}>
                <div className="min-w-0">
                  <div className="text-zinc-200 truncate">{f.rule_name}</div>
                  {f.error_message && <div className="text-[#FF3B30] mt-0.5">{f.error_message}</div>}
                  {!f.error_message && f.warnings?.length > 0 && <div className="text-[#FFB300] mt-0.5">{f.warnings[0]}</div>}
                  <div className="text-zinc-600 font-mono-data text-[10px] mt-0.5">{fmtDateTime(f.timestamp)}</div>
                </div>
                <StatusPill value={f.run_status} />
              </div>
            ))}
          </div>
        </Card>
      )}
    </section>
  );
}
